import {
    Button,
    Card, Dropdown, DropdownItemProps, Grid, GridColumn, GridRow,
    Header,
    HeaderContent,
    Icon,
    List, ListContent, ListItem,
    Modal,
    Placeholder, PlaceholderHeader, PlaceholderLine, PlaceholderParagraph,
    Table
} from "semantic-ui-react";
import HeaderSubHeader from "semantic-ui-react/dist/commonjs/elements/Header/HeaderSubheader";
import React, {useEffect, useState} from "react";
import {User} from "../../app/models/registration/user";
import {useStore} from "../../app/stores/store";
import {toast} from "react-toastify";
import {Entity} from "../../app/models/registration/entity";
import {isNaN} from "formik";
import ConfirmButton from "../../shared/ConfirmButton";


interface Props {
    show: boolean,
    userId: number,
    onClose: () => void,
}

function ManageUserEntitiesModal({show, userId, onClose}: Props) {
    const {adminStore} = useStore();
    const {commonStore} = useStore();
    const [userLoading, setUserLoading] = useState(true);
    const [userEntitiesLoading, setUserEntitiesLoading] = useState(true);
    const [entitiesLoading, setEntitiesLoading] = useState(true);
    const [capBlockUser, setCapBlockUser] = useState<User>();
    const [userEntities, setUserEntities] = useState<Entity[]>([]);
    const [entitiesDropDownProps] = useState<DropdownItemProps[]>([]);
    const [selectedEntity, setSelectedEntity] = useState<number | undefined>(undefined);
    const emptyOption = {key: '', value: 0, text: ''};

    const [allRolesList, setAllRolesList] = useState([emptyOption]);

    // Get user
    useEffect(() => {
        setUserLoading(true);
        adminStore.getUser(userId)
            .then(r => {
                setCapBlockUser(r);
            })
            .catch(err => toast.error(err, {theme: "colored"}))
            .finally(() => setUserLoading(false));
    }, [userId, adminStore]);

    // Get all entity roles for display.
    // This is cross-checked with the assigned user roles in the user's entities
    useEffect(() => {
        // order of operations is important- all roles first, then user roles
        commonStore.getEntityRoles()
            .then(r => {
                setAllRolesList(r);
            })
            .catch(err => toast.error(err, {theme: "colored"}))
            .finally(() => {
                setUserEntitiesLoading(false);
            });
    }, [commonStore]);

    // Get user entities
    useEffect(() => {
        if (allRolesList.length === 0) return;
        setUserEntitiesLoading(true);
        adminStore.getUserEntitiesById(userId)
            .then(r => {
                setUserEntities(r);
            })
            .catch(err => toast.error(err, {theme: "colored"}))
            .finally(() => setUserEntitiesLoading(false));
    }, [allRolesList,adminStore, userId]);

    // Get all entities for dropdown
    useEffect(() => {
        setEntitiesLoading(true);
        adminStore.getAllEntities()
            .then(r => {
                r.map((e, i) => {
                    entitiesDropDownProps.push({key: i, text: e.name, value: e.id});
                })
            })
            .catch(err => toast.error(err, {theme: "colored"}))
            .finally(() => setEntitiesLoading(false));
    }, [adminStore])

    // Add the user to selected entity and refresh the entity list
    const AddEntity = (async (entityId: number) => {
        if (userId === 0 || entityId === 0) return;
        adminStore.addUserEntity(userId, entityId!)
            .then((r) =>{ 
                adminStore.getUserEntitiesById(userId)
                    .then(r => {
                        setUserEntities(r);
                    })
                    .then(() => {
                        toast.success("User added to Entity successfully.", {theme: "colored"});
                    })
                })
            .catch(err => toast.error(err, {theme: "colored"}))
    });

    // Remove user from entity and refresh the list
    const RemoveEntity = (async (entityId: number) => {
        if (userId === 0 || entityId === 0) return;

        adminStore.deleteUserEntity(userId, entityId!)
            .then((r) =>{
                adminStore.getUserEntitiesById(userId)
                    .then(r => {
                        setUserEntities(r);
                    })
                    .then(() => {
                        toast.success("User removed from Entity successfully.", {theme: "colored"});
                    })
            })
            .catch(err => toast.error(err, {theme: "colored"}))
    });

    const entityHasRoles = (entity: Entity, role: string) => {
        if (entity.userRoles.length === 0) return false;
        return entity.userRoles.includes(role);
    };

    // Add the clicked role to the user's entity roles
    const addRoleToEntity = async (entity: Entity, role: number, roleName: string) => {
        adminStore.adduserEntityRole(userId, entity.id, role)
            .then((r) => {
                // No need to query again if the call was successful. Just push it.
                entity.userRoles.push(roleName);
                let l = [...userEntities];
                setUserEntities(l)
            })
            .then(() =>{
                toast.success("Role added successfully.", {theme: "colored"});
            })
            .catch(err => toast.error(err, {theme: "colored"}))

    }
    
    // Remove the clicked role from the user's entity roles
    const removeRoleFromEntity = async (entity: Entity, role: number, roleName: string) => {
        adminStore.removeuserEntityRole(userId, entity.id, role)
            .then((r) => {
                // No need to hit the server again if successful.
                let nr = entity.userRoles.filter(item => item !== roleName);
                entity.userRoles = nr;
                let l = [...userEntities];
                setUserEntities(l)
            })
            .then(() =>{
                toast.success("Role removied successfully.", {theme: "colored"});
            })
            .catch(err => toast.error(err, {theme: "colored"}))


    }

    return (
        <Modal open={show} centered={true} closeOnEscape={true} closeOnDimmerClick={false} size='large'>
            <Modal.Header>
                {userLoading ?
                    <>
                        <Placeholder>
                            <PlaceholderHeader image>
                                <PlaceholderLine/>
                                <PlaceholderLine/>
                            </PlaceholderHeader>
                        </Placeholder>
                    </>
                    :
                    <>
                        <Icon name={'user'}/>{capBlockUser?.firstName} {capBlockUser?.lastName} (ID:{capBlockUser?.id})
                    </>
                }
            </Modal.Header>
            <Modal.Content>
                <Card.Content>
                    <Grid container columns={'equal'}>
                        <GridRow>
                            <GridColumn textAlign={'left'}>
                                <Header as={'h3'}>

                                    <Icon name={'building'}/>
                                    <HeaderContent>
                                        Entity Role Assignment
                                        <HeaderSubHeader>Manage user's Entity memberships
                                        </HeaderSubHeader>
                                    </HeaderContent>

                                </Header>
                            </GridColumn>
                            <GridColumn textAlign={'right'}>
                                <Dropdown selection clearable search button value={undefined}
                                          options={entitiesDropDownProps} placeholder={'Select Entity'}
                                          onChange={(e, d) => {
                                              setSelectedEntity(parseInt(String(d.value)));
                                          }}/>
                                <Button
                                    disabled={selectedEntity === undefined || isNaN(selectedEntity) || entitiesLoading}
                                    icon={'plus'} color={'green'} label={'Add user to selected Entity'}
                                    onClick={() => {
                                        AddEntity(selectedEntity!);
                                    }}/>

                            </GridColumn>

                        </GridRow>
                    </Grid>
                </Card.Content>

                {userEntitiesLoading ?
                    <Placeholder>
                        <PlaceholderParagraph>
                            <PlaceholderLine length='full'/>
                            <PlaceholderLine length='full'/>
                            <PlaceholderLine length='full'/>
                            <PlaceholderLine length='full'/>
                            <PlaceholderLine length='full'/>
                        </PlaceholderParagraph>
                    </Placeholder>
                    :
                    <>
                        <Table singleLine selectable striped color={'green'}>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell><Icon name={'building'}/>Entity</Table.HeaderCell>
                                    <Table.HeaderCell><Icon name={'lock'}/>Role Assignments</Table.HeaderCell>
                                    <Table.HeaderCell textAlign={'center'} collapsing={true}><Icon name={'settings'}/>Actions</Table.HeaderCell>

                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {userEntities.map((entity: Entity, i) => (
                                    <Table.Row key={i}>
                                        <Table.Cell>{entity.name}</Table.Cell>
                                        <Table.Cell>
                                            <List horizontal>
                                                {allRolesList.map((role: any, i) => (

                                                    <ListItem as={'a'} key={role.value}
                                                              onClick={() => {
                                                                  entityHasRoles(entity, role.text) ?
                                                                      removeRoleFromEntity(entity, role.value, role.text) :
                                                                      addRoleToEntity(entity, role.value, role.text);

                                                              }}>
                                                        <Icon
                                                            name={entityHasRoles(entity, role.text) ? 'unlock' : 'lock'}
                                                            color={entityHasRoles(entity, role.text) ? 'green' : 'red'}/>
                                                        <ListContent>{role.text}</ListContent>
                                                    </ListItem>

                                                ))}
                                            </List>
                                        </Table.Cell>
                                        <Table.Cell textAlign={'center'} collapsing>
                                            <ConfirmButton id={entity.id} value={"Remove user from entity"} content={"This will remove the user-entity association, and all role assignments."} handleConfirm={function(id: number): void {
                                                RemoveEntity(id);
                                            } } />
                                        </Table.Cell>

                                    </Table.Row>)
                                )}
                            </Table.Body>

                        </Table>
                    </>
                }

            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose} primary>Close</Button>
            </Modal.Actions>
        </Modal>

    )
}

export default ManageUserEntitiesModal;